import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CustomDropZoneProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  Typography,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { UseMutateFunction } from 'react-query';
import { AxiosError } from 'axios';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { PresignedUrlResponse } from '../../../../models/PresignedUrl';
import {
  PutUploadFileToS3Request,
  usePutUploadToS3,
} from '../../../../api-refactored/s3-upload/usePutUploadFileToS3';
import { useGetDocumentUploadStatus } from '../../../../api-refactored/document/useGetDocumentUploadStatus';
import { usePutAttachBlacklistFile } from '../../../../api-refactored/candidate/usePutAttachBlacklistFile';
import { usePutStartBlacklistFileProcessing } from '../../../../api-refactored/candidate/usePutStartBlacklistFileProcessing';
import DocumentUploader from './DocumentUploader';
import { GenericErrorResponse } from '../../../../api-refactored/typings';
import DownloadTemplate from './DownloadTemplate';
import { useGetFileProcessingStatus } from '../../../../api-refactored/exam-configuration/useGetFileProcessingStatus';
import { FileProcessingState, FileUploadState } from '../../../../models/File';
import UploadingInProgress from '../../../../pages/admin/ExamConfiguration/CandidateHistory/UploadCandidateHistoriesModal/UploadingInProgress';
import FileProcessingError from '../../../../pages/admin/ExamConfiguration/CandidateHistory/UploadCandidateHistoriesModal/FileProcessingError';

import { typeTheme } from 'web-library/theme/helper/tssHelper';
const useStyles = makeStyles()((theme) => ({
  radioButton: {
    padding: '0px',
    margin: '0px',
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  buttonContainer: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  addIcon: {
    verticalAlign: 'sub',
    marginRight: 10,
    color: theme.color.icon.iconInverse,
  },
  selectSubjectText: {
    marginTop: theme.spacing(3),
  },
  customSelect: {
    marginTop: theme.spacing(2),
  },
  divider: {
    marginTop: theme.spacing(5),
  },
  downloadButton: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  downloadTemplateButton: {
    padding: '12px 48px',
    lineHeight: theme.spacing(4),
  },

  uploadErrorSection: {
    '& img': {
      width: 45,
      height: 45,
    },
  },
  fileProcessingErrorSpacing: {
    paddingRight: theme.spacing(3),
  },
  dropZoneHelperTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(4),
  },
  dropZoneHelperText: {
    color: theme.color.text.textSecondary,
  },
  fileBadgeParentContainer: {
    height: theme.spacing(4),
    marginBottom: theme.spacing(4),
    maxWidth: 514,
  },
  fileBadgeContainer: {
    height: theme.spacing(4),
    flexWrap: 'nowrap',
  },
  description: {
    marginBottom: theme.spacing(4),
  },
  uploadErrorDescription: {
    ...typeTheme(theme, 'body1bold'),
  },
  dropZone: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(6),
    width: '100%',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    justifySelf: 'start',
    marginRight: theme.spacing(2),
  },
  documentIcon: {
    color: theme.color.text.textTertiary,
  },
  filename: {
    width: theme.spacing(3),
    textOverflow: 'ellipsis',
    flexGrow: 2,
    fontWeight: 'bold',
    color: theme.color.link.linkDefault01,
  },
  fileSize: {
    width: theme.spacing(15),
    justifySelf: 'start',
    color: theme.color.text.textTertiary,
    paddingRight: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      paddingRight: theme.spacing(2),
    },
  },
  removeCell: {
    width: theme.spacing(12),
  },
  removeButton: {
    padding: 0,
    height: theme.spacing(4),
    color: theme.color.icon.iconTrash,
    '& .MuiButton-label > div': {
      flexWrap: 'nowrap',
    },
  },
  uploadingDescription: {
    paddingBottom: theme.spacing(7),
  },
  fileProcessingErrorSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: theme.spacing(4),
  },
}));

export type GridBreakPoints = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface BulkUploadProps extends CustomDropZoneProps {
  modalState: boolean;

  onDialogClose: () => void;

  displayUploadSuccessSnackbar: (numberOfRecords: number) => void;

  reloadUploadSuccess: () => void;
}

export enum ComponentFileProcessingState {
  SUCCESS = 'success',
  IDLE = 'idle',
  ERROR = 'error',
  PROCESSING = 'processing',
}

const uploadFile = (
  res: PresignedUrlResponse,
  uploadedFiles: UploadedFile[],
  uploadMutateFn: UseMutateFunction<
    void,
    AxiosError<GenericErrorResponse>,
    PutUploadFileToS3Request
  >
) => {
  const s3FormData = new FormData();
  s3FormData.append(res.key, uploadedFiles[0].file, res.key);
  const uploadedFile = import.meta.env.PROD ? uploadedFiles[0].file : s3FormData;
  uploadMutateFn({ data: uploadedFile, putUrl: res.presignedUrl });
};

const INITIAL_UPLOAD_PRESIGNED_DATA = {
  key: '',
  presignedUrl: '',
};

const BulkUploadBlacklistCandidatesModal: React.FC<BulkUploadProps> = (props: BulkUploadProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const { modalState, onDialogClose, displayUploadSuccessSnackbar, reloadUploadSuccess } = props;

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const [uploadPresigned, setUploadPresigned] = useState<PresignedUrlResponse>(
    INITIAL_UPLOAD_PRESIGNED_DATA
  );

  const [pollGetFileStatus, setPollGetFileStatus] = useState<boolean>(false);
  const [showUploadingHolder, setShowUploadingHolder] = useState<boolean>(false);

  const uploadMutation = usePutUploadToS3();
  const fileUuid = uploadPresigned?.key;

  const startAttachBlacklistFileMutation = usePutAttachBlacklistFile({
    onSuccess: (res) => {
      if (res.key && res.presignedUrl) {
        uploadFile(res, uploadedFiles, uploadMutation.mutate);
        setUploadPresigned(res);
      }
    },
  });

  const startFileProcessingMutation = usePutStartBlacklistFileProcessing({
    onSuccess: () => {
      setPollGetFileStatus(true);
    },
  });

  const getDocumentUploadStatus = useGetDocumentUploadStatus({
    fileKey: fileUuid || '',
    enabled: !!fileUuid && startFileProcessingMutation.isIdle,
    refetchInterval: 5000,
  });

  const getFileProcessingStatus = useGetFileProcessingStatus({
    fileKey: fileUuid || '',
    enabled: !!fileUuid && getDocumentUploadStatus.isSuccess && pollGetFileStatus,
    refetchInterval: 5000,
  });

  const fileProcessingStatus = getFileProcessingStatus.data?.status;
  const isFileProcessingError = fileProcessingStatus === FileProcessingState.ERROR;

  const uploadFileClickHandler = () => {
    setShowUploadingHolder(true);
    startFileProcessingMutation.reset();
    startAttachBlacklistFileMutation.mutate({
      fileName: uploadedFiles[0].file.name,
    });
  };

  const fileStatus = getDocumentUploadStatus.data?.fileStatus;
  const isUploadFailed = getDocumentUploadStatus.data?.fileStatus === FileUploadState.FAILED;

  useEffect(() => {
    if (fileStatus === FileUploadState.SUCCESS && fileUuid) {
      startFileProcessingMutation.mutate({ uuid: fileUuid });
    } else if (fileStatus === FileUploadState.FAILED && fileUuid) {
      setShowUploadingHolder(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileStatus, fileUuid, startFileProcessingMutation.mutate, setShowUploadingHolder]);

  const resetUploadAndProcessingApi = () => {
    setUploadPresigned(INITIAL_UPLOAD_PRESIGNED_DATA);
    startFileProcessingMutation.reset();
    startAttachBlacklistFileMutation.reset();
    uploadMutation.reset();
    getFileProcessingStatus.remove();
    getDocumentUploadStatus.remove();
    setPollGetFileStatus(false);
    setShowUploadingHolder(false);
  };

  useEffect(() => {
    if (fileProcessingStatus === FileProcessingState.SUCCESS && fileUuid) {
      resetUploadAndProcessingApi();
      onDialogClose();
      displayUploadSuccessSnackbar(getFileProcessingStatus.data?.numberOfRecords || 0);
      reloadUploadSuccess();
    } else if (fileProcessingStatus === FileProcessingState.ERROR && fileUuid) {
      setPollGetFileStatus(false);
      setShowUploadingHolder(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileProcessingStatus, setPollGetFileStatus, onDialogClose, fileUuid]);

  const conditionToShowDocumentUploader =
    uploadMutation.isIdle || isFileProcessingError || isUploadFailed;

  const disabledUploadButton = showUploadingHolder || !uploadedFiles.length;
  const disabledBackButton = showUploadingHolder;

  const onCancel = () => {
    onDialogClose();
    resetUploadAndProcessingApi();
  };

  return (
    <Dialog open={modalState} maxWidth="md" onClose={onDialogClose}>
      <DialogTitle>
        <Typography variant="h4">{t('admin->>CandidateBlacklist.BulkUploadTitle')}</Typography>
      </DialogTitle>
      <DialogContent>
        {conditionToShowDocumentUploader && (
          <>
            {isFileProcessingError ? (
              <FileProcessingError
                url={getFileProcessingStatus.data?.presignUrl || ''}
                classes={classes}
              />
            ) : (
              <Box>
                <DialogContentText>
                  {t('admin->>CandidateBlacklist.BulkUploadSubTitle')}
                </DialogContentText>
                <DownloadTemplate classes={classes} />
              </Box>
            )}

            <DocumentUploader
              key={getDocumentUploadStatus.data?.fileStatus}
              fileStatus={getDocumentUploadStatus.data?.fileStatus}
              classes={classes}
              translation={t}
              stateHandler={{
                setUploadedFileHandler: { uploadedFiles, setUploadedFiles },
              }}
              apiHooks={{
                uploadMutation,
              }}
              showUploadInstruction={isFileProcessingError}
            />
          </>
        )}
        {showUploadingHolder && (
          <UploadingInProgress
            classes={classes}
            uploadedFiles={uploadedFiles.map((uploadedFile) => uploadedFile.file)}
          />
        )}
      </DialogContent>
      <DialogActions id="oppadmin-dialog-action">
        <Box className={classes.buttonContainer}>
          <Button onClick={onCancel} variant="text" disabled={disabledBackButton}>
            {t('admin->>CandidateBlacklist.Cancel')}
          </Button>
          <Button
            startIcon={<Icon name={IconName.export} size="S" color="primary" />}
            type="button"
            onClick={uploadFileClickHandler}
            disabled={disabledUploadButton}
            id="button-upload"
          >
            {t('exam->>ExamConfiguration.UploadFile')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadBlacklistCandidatesModal;
